export const MAX_FIND_MY_BAG_PARTS = 10;
export const FIND_MY_BAG_QUERY_KEY = 'p';
export const FIND_MY_BAG_SESSION_KEY = 'bricksperbag_findmybag_selection_v1';

const ELEMENT_ID_PATTERN = /^\d{4,12}$/;

export function normalizeElementId(value) {
  const partId = String(value ?? '').trim();
  return ELEMENT_ID_PATTERN.test(partId) ? partId : null;
}

export function normalizePartQuantity(value) {
  const quantity = Number.parseInt(value, 10);
  if (!Number.isFinite(quantity)) return 1;
  return Math.min(99, Math.max(1, quantity));
}

export function normalizeFindMyBagSelection(parts, limit = MAX_FIND_MY_BAG_PARTS) {
  if (!Array.isArray(parts)) return [];

  const seen = new Set();
  const normalized = [];
  for (const part of parts) {
    const partId = normalizeElementId(part?.partId);
    if (!partId || seen.has(partId)) continue;
    seen.add(partId);
    normalized.push({
      ...part,
      partId,
      quantity: normalizePartQuantity(part?.quantity),
    });
    if (normalized.length >= limit) break;
  }
  return normalized;
}

// Compact and human-readable: p=6254045.2,300121. A missing quantity means 1.
export function serializeFindMyBagSelection(parts) {
  return normalizeFindMyBagSelection(parts)
    .map(part => `${part.partId}${part.quantity > 1 ? `.${part.quantity}` : ''}`)
    .join(',');
}

export function parseFindMyBagSelection(value) {
  if (!value) return [];
  const parts = String(value)
    .split(',')
    .map(token => {
      const [partId, quantity] = token.trim().split('.', 2);
      return { partId, quantity: quantity ?? 1, restoredFromUrl: true };
    });
  return normalizeFindMyBagSelection(parts);
}

export function mergeFindMyBagMetadata(parts, savedParts) {
  const savedById = new Map(normalizeFindMyBagSelection(savedParts).map(part => [part.partId, part]));

  return normalizeFindMyBagSelection(parts).map(part => ({
    ...(savedById.get(part.partId) || {}),
    ...part,
    partId: part.partId,
    quantity: part.quantity,
  }));
}

export function findBagPartMatches(selectedParts, matchedParts, partMatches = null) {
  const selectedById = new Map(normalizeFindMyBagSelection(selectedParts).map(part => [part.partId, part]));
  if (Array.isArray(partMatches) && partMatches.length > 0) {
    return partMatches.map(match => {
      const partId = String(match.partId ?? '').trim();
      const requested = normalizePartQuantity(match.requestedQuantity);
      const available = Math.max(0, Number.parseInt(match.availableQuantity, 10) || 0);
      return {
        ...(selectedById.get(partId) || { partId }),
        requested,
        available,
        status: match.status,
        enough: match.status === 'satisfied',
        difference: available - requested,
      };
    });
  }

  const availableById = new Map();
  for (const part of Array.isArray(matchedParts) ? matchedParts : []) {
    const partId = String(part?.partId ?? '').trim();
    if (!partId) continue;
    availableById.set(partId, Math.max(0, Number.parseInt(part.quantity, 10) || 0));
  }

  return normalizeFindMyBagSelection(selectedParts).map(part => {
    const available = availableById.has(part.partId) ? availableById.get(part.partId) : null;
    return {
      ...part,
      requested: part.quantity,
      available,
      status:
        available === null
          ? 'unknown'
          : available >= part.quantity
            ? 'satisfied'
            : available > 0
              ? 'short'
              : 'missing',
      enough: available === null ? null : available >= part.quantity,
      difference: available === null ? null : available - part.quantity,
    };
  });
}

export function summarizeBagMatch(partMatches) {
  if (partMatches.some(part => part.enough === false)) {
    return {
      label: 'Partial match',
      color: 'orange',
      description: 'This bag has some selected parts, but not every requested quantity.',
    };
  }
  if (partMatches.some(part => part.available === null)) {
    return {
      label: 'Parts matched',
      color: 'blue',
      description:
        'This bag contains every selected part. Quantity details are not available for every item.',
    };
  }
  if (partMatches.some(part => part.difference > 0)) {
    return {
      label: 'Contains enough',
      color: 'teal',
      description: 'This bag contains every selected part and at least the requested quantity.',
    };
  }
  return {
    label: 'Exact quantities',
    color: 'green',
    description: 'This bag contains every selected part in exactly the requested quantity.',
  };
}
